<template>
  <tr :class="{'requisite-row--child': child}">
    <td>
      <div class="detail__card card-info" >
        <div class="d-flex align-items-center gap-sm">
          <input v-if="selectable" type="checkbox" class="form-check-input flex-shrink-0" :checked="selected" @change="$emit('select', $event.target.checked)">
          <div>
            <PayMethodIcon icon-class="pay-method-icon-xl" :method="item.method"></PayMethodIcon>
          </div>
          <div>
            <div class="card-info__title">
              <span><strong>{{ item.currency }}</strong> {{ $t(`payment_options.${item.paymentOption}`) }}
            <span v-if="item.crossBorderCurrency">{{item.crossBorderCurrency}}</span> <span v-if="item.paymentSystem">{{ helper.trans('payment_systems.' + item.paymentSystem) }}</span>
          </span>

            </div>
            <template v-if="crossBorderSwift(item.raw)">
              <div class="card-info__name">{{ item.holderName }}</div>
              <div class="card-info__number">
                <span>{{ item.requisites }}</span>
                <RequisiteFillStatusDot v-if="hasFrozenSums(item)" :item="item" class="ms-1"></RequisiteFillStatusDot>
              </div>
              <div class="card-info__name">{{ $t('labels.raw_field.swift') }} {{ crossBorderSwift(item.raw) }}</div>
            </template>
            <template v-else>
            <div class="card-info__number d-flex align-items-start gap-1 flex-wrap">
              <a v-if="item.paymentOption === PaymentOption.SBP_QR && item.requisites?.startsWith('http')" :href="item.requisites" target="_blank">{{ helper.domain(item.requisites) }}</a>
              <span v-else-if="item.paymentOption === PaymentOption.MANUAL_SBP_QR" class="text-muted">—</span>
              <a v-else-if="item.paymentOption === PaymentOption.CLICK_UZ_QR && item.requisites.startsWith('http')" :href="item.requisites" target="_blank">{{ helper.trans('labels.my_click_uz') }}</a>
              <a v-else-if="item.paymentOption === PaymentOption.TIPS && item.requisites.startsWith('http')" :href="item.requisites" target="_blank">{{ helper.domain(item.requisites) }}</a>
              <template v-else-if="item.paymentOption === PaymentOption.VIET_QR">
                <span
                  v-b-tooltip.hover
                  class="font-monospace text-break flex-grow-1"
                  style="min-width: 0"
                  :title="vietQrEmvFromRaw(item.raw) || undefined"
                >{{ truncateVietQrEmvDisplay(vietQrEmvFromRaw(item.raw)) || '—' }}</span>
                <copy-button v-if="vietQrEmvFromRaw(item.raw)" :value="vietQrEmvFromRaw(item.raw)" class="flex-shrink-0 align-self-center"></copy-button>
              </template>
              <template v-else-if="item.paymentOption === PaymentOption.IND_QR">
                <span
                  v-b-tooltip.hover
                  class="font-monospace text-break flex-grow-1"
                  style="min-width: 0"
                  :title="qrisEmvFromRaw(item.raw) || undefined"
                >{{ truncateVietQrEmvDisplay(qrisEmvFromRaw(item.raw)) || '—' }}</span>
                <copy-button v-if="qrisEmvFromRaw(item.raw)" :value="qrisEmvFromRaw(item.raw)" class="flex-shrink-0 align-self-center"></copy-button>
              </template>
              <span v-else>{{ item.requisites }}</span>
              <RequisiteFillStatusDot v-if="hasFrozenSums(item)" :item="item" class="align-self-center ms-1"></RequisiteFillStatusDot>
            </div>
            <div class="card-info__name">
              {{ item.holderName }}
            </div>
            </template>
          </div>
        </div>

      </div>
    </td>
    <td class="detail__flex">
      <div class="rate-info__mobile-label">{{$t('labels.device')}}</div>
      <span v-if="item.device" class="d-flex">
        <img :src="require(`@/apps/public/assets/img/device-${item.device.status}.svg`)">
        {{ item.device?.name }}
      </span>
    </td>
    <td class="detail__flex">
      <div class="rate-info__mobile-label">{{$t('labels.req_group')}}</div>
      {{ item.group?.name }}
    </td>
    <td class="detail__flex">
      <div class="rate-info__mobile-label">{{$t('labels.sum_limits')}}</div>
      <div class="sum-limits">
        <span v-if="item.minPayInDealSum">{{ helper.trans('labels.from_limit', {sum: numberFormatter.formatNumber(item.minPayInDealSum), currency: item.minPayInDealSum!.currency })}}<ReuseMinCapBadge :item="item"></ReuseMinCapBadge></span>
        <span v-if="item.maxPayInDealSum">{{ helper.trans('labels.to_limit', {sum: numberFormatter.formatNumber(item.maxPayInDealSum), currency: item.maxPayInDealSum!.currency })}}</span>
      </div>
    </td>
    <td>
      <div class="detail__limits">
        <div class="rate-info__mobile-label">{{$t('labels.volume_limits')}}</div>
        <div class="detail__limit">
          <div class="detail__limit-type">{{$t('labels.day')}}</div>
          <div class="detail__limit-container" v-b-tooltip.hover :title="sumTitle('labels.day', item.dayTradesSum, item.dayTradesFrozenSum)">
            <RequisiteSumFill v-if="hasFrozenSums(item)" :total="item.dayTradesSum" :frozen="item.dayTradesFrozenSum" :limit="item.dayTradesSumLimit" :active="item.isActive"></RequisiteSumFill>
            <div v-else class="detail__limit-count" :style="{width: (parseInt(item.dayTradesSum.amount)/parseInt(item.dayTradesSumLimit.amount) * 100) + '%'}" :class="{'bg-attention': isDayLimitReached(item)}"></div>
            <div class="limits-values">
              {{ numberFormatter.formatNumber(item.dayTradesSum) }}/{{ numberFormatter.formatNumber(item.dayTradesSumLimit) }}
            </div>
          </div>
        </div>
        <div class="detail__limit">
          <div class="detail__limit-type">{{$t('labels.month')}}</div>
          <div class="detail__limit-container" v-b-tooltip.hover :title="sumTitle('labels.month', item.monthTradesSum, item.monthTradesFrozenSum)">
            <RequisiteSumFill v-if="hasFrozenSums(item)" :total="item.monthTradesSum" :frozen="item.monthTradesFrozenSum" :limit="item.monthTradesSumLimit" :active="item.isActive"></RequisiteSumFill>
            <div v-else class="detail__limit-count" :style="{width: (parseInt(item.monthTradesSum.amount)/parseInt(item.monthTradesSumLimit.amount) * 100) + '%'}" :class="{'bg-attention': isMonthLimitReached(item)}"></div>
            <div class="limits-values">
              {{ numberFormatter.formatNumber(item.monthTradesSum) }}/{{ numberFormatter.formatNumber(item.monthTradesSumLimit) }}
            </div>
          </div>
        </div>
      </div>
    </td>
    <td>
      <div class="detail__limits count-limits">
        <div class="rate-info__mobile-label">{{$t('labels.count_limits')}}</div>
        <div class="detail__limit">
          <div class="detail__limit-type">{{$t('labels.day')}}</div>
          <div class="detail__limit-container" v-b-tooltip.hover :title="$t('labels.day')">
            <div class="detail__limit-count" :style="{width: (item.dayTradesCountLimit === 0 ? 0 : parseInt(item.dayTradesCount)/parseInt(item.dayTradesCountLimit) * 100) + '%'}" :class="{'bg-attention': item.dayTradesCountLimit > 0 && item.dayTradesCount/item.dayTradesCountLimit >= 0.9}"></div>
            <div class="limits-values">
              {{ numberFormatter.formatNumber(item.dayTradesCount) }}/{{ numberFormatter.formatNumber(item.dayTradesCountLimit) }}
            </div>
          </div>
        </div>
        <div class="detail__limit">
          <div class="detail__limit-type">{{$t('labels.month')}}</div>
          <div class="detail__limit-container" v-b-tooltip.hover :title="$t('labels.month')">
            <div class="detail__limit-count" :style="{width: (item.monthTradesCountLimit === 0 ? 0 : parseInt(item.monthTradesCount)/parseInt(item.monthTradesCountLimit) * 100) + '%'}" :class="{'bg-attention': item.monthTradesCountLimit > 0 && item.monthTradesCount/item.monthTradesCountLimit >= 0.9}"></div>
            <div class="limits-values">
              {{ numberFormatter.formatNumber(item.monthTradesCount) }}/{{ numberFormatter.formatNumber(item.monthTradesCountLimit) }}
            </div>
          </div>
        </div>
      </div>
    </td>
    <td class="detail__flex">
      <div class="rate-info__mobile-label">{{$t('labels.concurrent_trader_count_short')}}</div>
      <span v-if="item.concurrentTradesCount">{{ numberFormatter.formatNumber(item.concurrentTradesCount) }}</span>
    </td>
    <td class="detail__flex">
      <div class="rate-info__mobile-label">
        {{ $t('labels.status') }}
      </div>
      <div class="switcher">
        <label class="switch">
          <input type="checkbox" v-b-tooltip.hover :title="item.isActive ? helper.trans('requisites.active') : helper.trans('requisites.inactive')" v-model="item.isActive" @change="$emit('toggle', $event, item)">
          <span class="slider"></span>
        </label>
      </div>
    </td>
    <td>
      <div class="detail__controls" style="">
        <a v-if="item.device" href="#" @click.prevent="$emit('showLogs', item.device)" class="btn detail__btn">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3.505 2.365C5.32653 2.12142 7.16226 1.99948 9 2C10.863 2 12.697 2.124 14.495 2.365C15.742 2.532 16.675 3.473 16.93 4.633C16.7394 4.59745 16.5466 4.57441 16.353 4.564C14.7855 4.47838 13.2145 4.47838 11.647 4.564C9.229 4.696 7.5 6.727 7.5 8.998V11.238C7.5 12.651 8.17 13.973 9.26 14.8L6.28 17.78C6.17511 17.8848 6.04153 17.9561 5.89614 17.985C5.75074 18.0139 5.60004 17.999 5.46308 17.9423C5.32611 17.8856 5.20903 17.7895 5.12661 17.6663C5.04419 17.5431 5.00013 17.3982 5 17.25V13.807C4.499 13.759 4 13.701 3.505 13.635C2.033 13.438 1 12.162 1 10.72V5.28C1 3.839 2.033 2.562 3.505 2.365Z" fill="#F7F9FC"></path>
            <path d="M14 6C13.238 6 12.48 6.02 11.729 6.062C10.157 6.148 9 7.472 9 8.998V11.238C9 12.757 10.147 14.077 11.71 14.173C11.924 14.186 12.138 14.197 12.352 14.207C12.552 14.216 12.737 14.297 12.87 14.431L15.22 16.781C15.3249 16.8858 15.4586 16.9572 15.6041 16.986C15.7496 17.0149 15.9004 16.9999 16.0374 16.9431C16.1744 16.8863 16.2915 16.7901 16.3738 16.6667C16.4562 16.5433 16.5001 16.3983 16.5 16.25V14.18C17.953 13.985 19 12.717 19 11.265V8.998C19 7.472 17.843 6.148 16.271 6.062C15.5147 6.02068 14.7574 6.00001 14 6Z" fill="#F7F9FC"></path>
          </svg>
        </a>
        <a v-if="item.isWhiteTriangle && store.getters.getAppData?.fraudexEnabled" href="#" @click.prevent="$emit('fraudexFeedback', item)" v-b-tooltip.hover :title="$t('fraudex.leave_feedback')" class="btn detail__btn">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 2C1.44772 2 1 2.44772 1 3V13C1 13.5523 1.44772 14 2 14H4V17.5858C4 17.851 4.10536 18.1054 4.29289 18.2929L7.70711 21.7071C7.89464 21.8946 8.149 22 8.41421 22H18C18.5523 22 19 21.5523 19 21V3C19 2.44772 18.5523 2 18 2H2Z" fill="#F7F9FC"/>
            <path d="M5 7C5 6.44772 5.44772 6 6 6H14C14.5523 6 15 6.44772 15 7C15 7.55228 14.5523 8 14 8H6C5.44772 8 5 7.55228 5 7Z" fill="white"/>
            <path d="M5 10C5 9.44772 5.44772 9 6 9H11C11.5523 9 12 9.44772 12 10C12 10.5523 11.5523 11 11 11H6C5.44772 11 5 10.5523 5 10Z" fill="white"/>
          </svg>
          <span>{{ $t('fraudex.leave_feedback') }}</span>
        </a>
        <a href="#" @click.prevent="$emit('edit', item)" class="btn detail__btn detail__redact">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1.43302 17.9172L2.69502 14.7622C2.89605 14.2592 3.19714 13.8023 3.58002 13.4192L13.9898 3.01046L16.9903 6.01046L6.58102 16.4192C6.19799 16.8025 5.74108 17.1039 5.23802 17.3052L2.08302 18.5672C1.99216 18.6036 1.89262 18.6125 1.79674 18.5928C1.70087 18.5731 1.61287 18.5258 1.54366 18.4566C1.47446 18.3874 1.42708 18.2994 1.40742 18.2035C1.38775 18.1076 1.39665 18.0081 1.43302 17.9172Z" fill="#F7F9FC"></path>
            <path d="M18.1213 4.0012C18.1213 4.56381 17.8978 5.10338 17.5 5.5012L14.5 2.5012C14.8978 2.10338 15.4374 1.87988 16 1.87988C16.5626 1.87988 17.1022 2.10338 17.5 2.5012C17.8978 2.89903 18.1213 3.43859 18.1213 4.0012Z" fill="#F7F9FC"></path>
          </svg>
        </a>
        <a href="#" @click.prevent="$emit('remove', item)" class="btn detail__btn detail__remove">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 3C1.73478 3 1.48043 3.10536 1.29289 3.29289C1.10536 3.48043 1 3.73478 1 4V5C1 5.26522 1.10536 5.51957 1.29289 5.70711C1.48043 5.89464 1.73478 6 2 6H18C18.2652 6 18.5196 5.89464 18.7071 5.70711C18.8946 5.51957 19 5.26522 19 5V4C19 3.73478 18.8946 3.48043 18.7071 3.29289C18.5196 3.10536 18.2652 3 18 3H2Z" fill="#F7F9FC"></path>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M2 7.5H18L17.189 15.21C17.1371 15.7015 16.905 16.1564 16.5376 16.487C16.1701 16.8175 15.6933 17.0003 15.199 17H4.802C4.30774 17.0003 3.8309 16.8175 3.46343 16.487C3.09596 16.1564 2.8639 15.7015 2.812 15.21L2 7.5ZM7.22 9.22C7.36063 9.07955 7.55125 9.00066 7.75 9.00066C7.94875 9.00066 8.13937 9.07955 8.28 9.22L10 10.94L11.72 9.22C11.7887 9.14631 11.8715 9.08721 11.9635 9.04622C12.0555 9.00523 12.1548 8.98318 12.2555 8.98141C12.3562 8.97963 12.4562 8.99816 12.5496 9.03588C12.643 9.0736 12.7278 9.12974 12.799 9.20096C12.8703 9.27218 12.9264 9.35701 12.9641 9.4504C13.0018 9.54379 13.0204 9.64382 13.0186 9.74452C13.0168 9.84522 12.9948 9.94454 12.9538 10.0365C12.9128 10.1285 12.8537 10.2113 12.78 10.28L11.06 12L12.78 13.72C12.8537 13.7887 12.9128 13.8715 12.9538 13.9635C12.9948 14.0555 13.0168 14.1548 13.0186 14.2555C13.0204 14.3562 13.0018 14.4562 12.9641 14.5496C12.9264 14.643 12.8703 14.7278 12.799 14.799C12.7278 14.8703 12.643 14.9264 12.5496 14.9641C12.4562 15.0018 12.3562 15.0204 12.2555 15.0186C12.1548 15.0168 12.0555 14.9948 11.9635 14.9538C11.8715 14.9128 11.7887 14.8537 11.72 14.78L10 13.06L8.28 14.78C8.13783 14.9125 7.94978 14.9846 7.75548 14.9812C7.56118 14.9777 7.37579 14.899 7.23838 14.7616C7.10097 14.6242 7.02225 14.4388 7.01883 14.2445C7.0154 14.0502 7.08752 13.8622 7.22 13.72L8.94 12L7.22 10.28C7.07955 10.1394 7.00066 9.94875 7.00066 9.75C7.00066 9.55125 7.07955 9.36063 7.22 9.22Z" fill="#F7F9FC"></path>
          </svg>
        </a>
      </div>
    </td>
  </tr>
</template>
<script lang="ts">
import {defineComponent, PropType} from 'vue'
import {useStore} from "vuex";
import {$t, helper} from "@/service/CommonHelper";
import TraderRequisitesDTO from "@/models/TraderRequisitesDTO";
import PayMethodIcon from "@/components/PayMethodIcon.vue";
import {PaymentOption} from "@/models/enum/PaymentOption";
import {numberFormatter} from "@/service/NumberFormatter";
import {qrisEmvFromRaw, truncateVietQrEmvDisplay, vietQrEmvFromRaw} from "@/service/vietQrRawHelpers";
import {crossBorderSwift} from "@/service/crossBorderSwift";
import CopyButton from "@/apps/public/components/CopyButton.vue";
import RequisiteSumFill from "@/apps/public/components/RequisiteSumFill.vue";
import RequisiteFillStatusDot from "@/components/RequisiteFillStatusDot.vue";
import ReuseMinCapBadge from "@/apps/public/components/ReuseMinCapBadge.vue";
import {hasFrozenSums, sumTitle} from "@/service/requisiteFrozenSums";

export default defineComponent({
  methods: {$t},
  components: {PayMethodIcon, CopyButton, RequisiteSumFill, RequisiteFillStatusDot, ReuseMinCapBadge},
  props: {
    item: {type: Object as PropType<TraderRequisitesDTO>, required: true},
    child: {type: Boolean, default: false},
    selectable: {type: Boolean, default: false},
    selected: {type: Boolean, default: false},
  },
  emits: ['toggle', 'showLogs', 'fraudexFeedback', 'edit', 'remove', 'select'],
  setup() {
    const store = useStore()

    const isMonthLimitReached = (item: TraderRequisitesDTO) => {
      return parseInt(item.monthTradesSum.amount) / parseInt(item.monthTradesSumLimit.amount) >= 0.9
    }

    const isDayLimitReached = (item: TraderRequisitesDTO) => {
      return parseInt(item.dayTradesSum.amount) / parseInt(item.dayTradesSumLimit.amount) >= 0.9
    }

    return {
      store,
      helper,
      numberFormatter,
      PaymentOption,
      isMonthLimitReached,
      isDayLimitReached,
      hasFrozenSums,
      sumTitle,
      vietQrEmvFromRaw,
      qrisEmvFromRaw,
      truncateVietQrEmvDisplay,
      crossBorderSwift,
    }
  }
})
</script>
<style scoped>
tr.requisite-row--child > td:first-child {
  padding-left: 56px;
}
</style>
